import { jsPDF } from "jspdf";

// Brand palette
const GOLD = [212, 160, 23]; // #D4A017
const GOLD_LIGHT = [245, 158, 11]; // #F59E0B
const BLACK = [15, 15, 15];
const DARK = [45, 45, 45];
const GRAY = [110, 110, 110];
const LIGHT_BG = [247, 247, 247];

// Load an image URL into a dataURL (for embedding the logo). Resolves null on failure.
const loadImageAsDataURL = (url) =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve({ data: canvas.toDataURL("image/jpeg", 0.92), w: img.naturalWidth, h: img.naturalHeight });
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    } catch (e) {
      resolve(null);
    }
  });

export const generateTonnagePDF = async (d) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth(); // ~595
  const M = 40; // margin
  let y = 0;

  // ===== Header band =====
  doc.setFillColor(...BLACK);
  doc.rect(0, 0, W, 92, "F");

  // Logo (optional)
  const logo = await loadImageAsDataURL("/images/logo-marco.png");
  let textX = M;
  if (logo) {
    const h = 52;
    const w = (logo.w / logo.h) * h;
    try {
      doc.addImage(logo.data, "JPEG", M, 20, w, h);
      textX = M + w + 14;
    } catch (e) {
      textX = M;
    }
  }

  doc.setTextColor(...GOLD_LIGHT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("MARCO AIR CONDITION", textX, 42);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Experience Comfort Cooling  |  45+ Years of HVAC Excellence", textX, 60);
  doc.setTextColor(...GOLD);
  doc.setFontSize(9);
  doc.text("Call: +91 98258 84460   |   Surat, Gujarat", textX, 76);

  // Gold accent line
  doc.setFillColor(...GOLD_LIGHT);
  doc.rect(0, 92, W, 5, "F");

  y = 128;

  // ===== Report title =====
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("AC Tonnage Calculation Report", M, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  if (d.refId) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...GOLD);
    doc.text(`Ref: ${d.refId}`, W - M, y - 8, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...GRAY);
  }
  doc.text(`${d.dateTime || d.date}`, W - M, y + 5, { align: "right" });

  y += 14;
  doc.setDrawColor(220, 220, 220);
  doc.line(M, y, W - M, y);
  y += 22;

  // ===== Customer details =====
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(M, y - 14, W - 2 * M, 44, 6, 6, "F");
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text("PREPARED FOR", M + 14, y);
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(d.name || "Customer", M + 14, y + 16);
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(`Mobile: +91 ${d.mobile}`, W - M - 14, y + 16, { align: "right" });
  y += 52;

  // ===== Recommended result highlight =====
  const boxH = 74;
  doc.setFillColor(...BLACK);
  doc.roundedRect(M, y, W - 2 * M, boxH, 8, 8, "F");
  doc.setFillColor(...GOLD_LIGHT);
  doc.roundedRect(M, y, 6, boxH, 3, 3, "F");

  doc.setTextColor(...GOLD_LIGHT);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("RECOMMENDED COOLING CAPACITY", M + 22, y + 22);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(30);
  doc.setTextColor(255, 255, 255);
  doc.text(`${d.tonnage.toFixed(2)} Tons`, M + 22, y + 52);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...GOLD_LIGHT);
  doc.text(`${d.totalBTU.toLocaleString("en-IN")} BTU/hr`, M + 22, y + 66);

  // right side - suggested unit
  doc.setFontSize(9);
  doc.setTextColor(200, 200, 200);
  doc.text("Suggested Unit", W - M - 16, y + 26, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...GOLD_LIGHT);
  const recLabel = doc.splitTextToSize(d.recSizeLabel, 200);
  doc.text(recLabel, W - M - 16, y + 46, { align: "right" });

  y += boxH + 26;

  // ===== Two-column: Room details & Heat factors =====
  const colW = (W - 2 * M - 20) / 2;
  const sectionTop = y;

  const drawSection = (x, title, rows) => {
    let yy = sectionTop;
    doc.setFillColor(...GOLD_LIGHT);
    doc.rect(x, yy, colW, 22, "F");
    doc.setTextColor(...BLACK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(title, x + 10, yy + 15);
    yy += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    rows.forEach((r, i) => {
      if (i % 2 === 0) {
        doc.setFillColor(...LIGHT_BG);
        doc.rect(x, yy, colW, 20, "F");
      }
      doc.setTextColor(...GRAY);
      doc.text(r[0], x + 10, yy + 14);
      doc.setTextColor(...BLACK);
      doc.setFont("helvetica", "bold");
      doc.text(String(r[1]), x + colW - 10, yy + 14, { align: "right" });
      doc.setFont("helvetica", "normal");
      yy += 20;
    });
    return yy;
  };

  const endLeft = drawSection(M, "Room Details", [
    ["Length", `${d.length} ft`],
    ["Breadth", `${d.breadth} ft`],
    ["Height", `${d.height} ft`],
    ["Volume", `${Math.round(d.volume).toLocaleString("en-IN")} cu.ft`],
    ["Peak Summer Temp", `${d.temp} °C`],
  ]);

  const endRight = drawSection(M + colW + 20, "Heat Load Factors", [
    ["Roof Exposure", d.roofLabel],
    ["Window Exposure", d.windowLabel],
    ["Occupants", String(d.occupants)],
    ["Heat Sources", d.equipmentLabel],
    ["Temp Factor", `x ${d.fTemp.toFixed(3)}`],
  ]);

  y = Math.max(endLeft, endRight) + 24;

  // ===== Calculation breakdown =====
  doc.setFillColor(...GOLD_LIGHT);
  doc.rect(M, y, W - 2 * M, 22, "F");
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Calculation Breakdown", M + 10, y + 15);
  y += 22;

  const breakdown = [
    ["Base Load (Volume x 10)", `${Math.round(d.baseBTU).toLocaleString("en-IN")} BTU`],
    [`Subtotal after Temperature Factor (x ${d.fTemp.toFixed(3)})`, `${Math.round(d.subtotal).toLocaleString("en-IN")} BTU`],
  ];
  if (d.roofExtra > 0) breakdown.push([`Roof Exposure Gain (+${Math.round((d.roofExtra / d.subtotal) * 100)}%)`, `+ ${Math.round(d.roofExtra).toLocaleString("en-IN")} BTU`]);
  if (d.windowExtra > 0) breakdown.push(["Large West-facing Glass", `+ ${d.windowExtra.toLocaleString("en-IN")} BTU`]);
  if (d.occupantExtra > 0) breakdown.push([`Extra Occupants (${d.occupants - 2})`, `+ ${d.occupantExtra.toLocaleString("en-IN")} BTU`]);
  if (d.equipmentExtra > 0) breakdown.push([d.equipmentLabel, `+ ${d.equipmentExtra.toLocaleString("en-IN")} BTU`]);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  breakdown.forEach((r, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(...LIGHT_BG);
      doc.rect(M, y, W - 2 * M, 20, "F");
    }
    doc.setTextColor(...GRAY);
    doc.text(r[0], M + 10, y + 14);
    doc.setTextColor(...BLACK);
    doc.text(r[1], W - M - 10, y + 14, { align: "right" });
    y += 20;
  });
  // total row
  doc.setFillColor(...BLACK);
  doc.rect(M, y, W - 2 * M, 22, "F");
  doc.setTextColor(...GOLD_LIGHT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Total Heat Load", M + 10, y + 15);
  doc.text(`${d.totalBTU.toLocaleString("en-IN")} BTU/hr`, W - M - 10, y + 15, { align: "right" });
  y += 34;

  // ===== Recommended brands =====
  doc.setTextColor(...BLACK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Recommended AC Brands", M, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  const brandLines = doc.splitTextToSize(d.brands.join("  •  "), W - 2 * M);
  doc.text(brandLines, M, y);
  y += brandLines.length * 13 + 6;
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  const disc = doc.splitTextToSize(
    "* This is an estimate for guidance. Final sizing may vary with insulation, climate and usage. Contact MARCO AIR CONDITION for a precise site survey.",
    W - 2 * M
  );
  doc.text(disc, M, y);

  // ===== Footer band =====
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFillColor(...BLACK);
  doc.rect(0, pageH - 54, W, 54, "F");
  doc.setFillColor(...GOLD_LIGHT);
  doc.rect(0, pageH - 58, W, 4, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("MARCO AIR CONDITION", M, pageH - 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD_LIGHT);
  doc.text("Phone / WhatsApp: +91 98258 84460", M, pageH - 20);
  doc.text("marcohvacsurat@gmail.com  |  customercare@marcoaircondition.com", W - M, pageH - 34, { align: "right" });
  doc.setTextColor(200, 200, 200);
  doc.text("Nanpura, Surat - 395001, Gujarat", W - M, pageH - 20, { align: "right" });

  const fileName = `MARCO-AC-Tonnage-${(d.name || "Report").replace(/[^a-z0-9]/gi, "_")}.pdf`;
  doc.save(fileName);
  return fileName;
};
